import { useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { LinkContainer } from "react-router-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";

import { Loading, OnboardingTooltip } from "components";
import EnvelopesOverview from "components/EnvelopesOverview/EnvelopesOverview";
import { IsGeometry } from "components/ExerciseInfo/IsGeometry";
import { PrepareSupplies } from "components/ExerciseInfo/PrepareSupplies";
import { SelfReflection } from "components/ExerciseInfo/SelfReflection";
import { Lightbox } from "components/Lightbox";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { clearSessionId, createEmptyResults, setSelectedTime } from "store/slices/exercise";
import { resetAllTimers } from "store/slices/timer";
import { getUserEmail, selectPreferredSubject, selectSessionCount } from "store/slices/user";

import { getFirebaseAssetURL } from "utils/getFirebaseAssetURL";
import { processApiError } from "utils/processApiError";
import { getSessionFromStorage, saveSessionToStorage } from "utils/sessionStorageHandler";

import type { SessionCreateMutation } from "api/generated";
import { useSessionCloseMutation, useSessionCreateMutation, useSessionStartMutation } from "api/generated";

import IconMinus from "images/icons/minus.svg";
import IconPlus from "images/icons/plus.svg";

import { SoundTestModal } from "components/Modal/SoundTestModal";
import TagManager from "react-gtm-module";
import { useSessionStorageAssignmentId } from "utils/hooks/useSessionStorageAssignmentId";
import { tooltipTexts } from "./TooltipTexts";

const setHighlightClass = (element: HTMLElement) => {
  element.classList.add("highlight-tooltip");
};

const resetHighlightClass = (element: HTMLElement) => {
  element.classList.remove("highlight-tooltip");
};

interface IPageData {
  subjects: null | string[];
  questionCount: number;
  envelopes: Record<"red" | "orange" | "green", number> | null;
  easySubjectsCount: number;
  mood: { mood: number | null; moodText: string | null; selfReflectionText: string | null };
}

const TIME_STEP = 5;
const TIME_MIN = 25;
const TIME_MAX = 90;

const Intro = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(["exercise"]);
  const dispatch = useAppDispatch();
  const sessionsCount = useAppSelector(selectSessionCount);
  const preferredSubject = useAppSelector(selectPreferredSubject);
  const userEmail = useAppSelector(getUserEmail);
  const location = useLocation();

  const [createSession, { error: apiCreateError }] = useSessionCreateMutation();
  const [startSession, { isLoading: startingSession, error: apiStartError }] = useSessionStartMutation();
  const [closeSession, { error: apiCloseError }] = useSessionCloseMutation();

  const [loadingState, setLoadingState] = useState({ isUpdate: true, isInitialLoad: true });
  const [selectedTime, selectTime] = useState<number | undefined>(undefined);
  const [selectedSubjectToStart, setSelectedSubjectToStart] = useState<string | null>(null);
  const [geometryImages, setGeometryImages] = useState<string[]>([]);
  const [assignmentAudioFiles, setAssignmentAudioFiles] = useState<string[]>([]);
  const [apiData, setApiData] = useState<IPageData>({
    subjects: null,
    questionCount: 0,
    envelopes: null,
    easySubjectsCount: 0,
    mood: { mood: null, moodText: null, selfReflectionText: null },
  });
  const [shownTooltipIndex, setShownTooltipIndex] = useState<number | null>(null);
  const [isSoundTestModalOpen, setIsSoundTestModalOpen] = useState(false);
  const [isNoAssignmentsModalOpen, setIsNoAssignmentsModalOpen] = useState(false);
  const assignmentId = useSessionStorageAssignmentId();

  const loadingData = useRef(false);
  const isSessionClosed = useRef(false);
  const highlightedElement = useRef<HTMLElement | null>(null);
  const shownTooltips = useRef<number[]>([]);
  const processingTooltip = useRef<boolean>(false);

  useEffect(() => {
    if (loadingState.isInitialLoad) return;

    if (typeof sessionsCount !== "number" || sessionsCount === 0) {
      showNextTooltip();
    }
  }, [loadingState.isInitialLoad, sessionsCount]);

  useEffect(() => {
    const sessionData = getSessionFromStorage(userEmail);
    const sessionId = sessionData?.sessionId;

    if (typeof sessionId === "string" && sessionData !== null) {
      navigate(`/cviceni/${sessionId}`);
      return;
    }

    dispatch(clearSessionId());
    selectTime(TIME_MIN);
  }, []);

  useEffect(() => {
    if (apiCreateError === undefined) return;

    processApiError(apiCreateError);
  }, [apiCreateError]);

  useEffect(() => {
    if (apiStartError === undefined) return;

    processApiError(apiStartError);
  }, [apiStartError]);

  useEffect(() => {
    if (
      typeof apiCloseError === "object" &&
      apiCloseError !== null &&
      "message" in apiCloseError &&
      typeof apiCloseError.message === "string" &&
      (apiCloseError.message.toLocaleLowerCase().includes("no session to be closed") ||
        apiCloseError.message.toLocaleLowerCase().includes("session created but not started"))
    )
      return;

    processApiError(apiCloseError);
  }, [apiCloseError]);

  const sortSubjects = (subjects: string[]) => {
    subjects.sort();

    if (typeof preferredSubject === "string" && subjects.includes(preferredSubject)) {
      /* Moves preferred subject to last position */
      subjects.push(subjects.splice(subjects.indexOf(preferredSubject), 1)[0]);
    }

    return subjects;
  };

  const countEnvelopes = (data: SessionCreateMutation) => {
    const result = {
      red: 0,
      orange: 0,
      green: 0,
    };

    for (const assignment of data.sessionCreate.userAssignments) {
      switch (assignment.envelope) {
        case "red":
          result.red++;
          break;
        case "yellow":
          result.orange++;
          break;
        case "green":
          result.green++;
          break;
        default:
      }
    }

    return result;
  };

  useEffect(() => {
    if (typeof selectedTime !== "number") return;
    loadingData.current = true;

    setGeometryImages([]);
    setLoadingState(state => ({
      isUpdate: true,
      isInitialLoad: state.isInitialLoad,
    }));

    if (!isSessionClosed.current) {
      closeSession({ mood: 0, moodText: "", selfReflectionText: "", assignmentsClose: [] })
        .catch(console.error)
        .finally(() => {
          isSessionClosed.current = true;
          createNewSession(selectedTime, assignmentId);
        });
    } else {
      createNewSession(selectedTime, assignmentId);
    }
  }, [selectedTime]);

  const createNewSession = (time: number, assignmentId?: string | null) => {
    createSession({ lengthPreference: time * 60, assignmentId })
      .then(data => {
        if ("data" in data && data.data) {
          for (const assignment of data.data.sessionCreate.userAssignments) {
            const imagePath = assignment.assignment?.assignment?.printingAttachment;
            const audioPath = assignment.assignment?.assignment?.assignmentAudio;

            if (typeof imagePath === "string") {
              getFirebaseAssetURL(imagePath)
                .then(url => {
                  if (url === undefined) return;
                  setGeometryImages(images => {
                    const copy = [...images];
                    copy.push(url);
                    return copy;
                  });
                })
                .catch(() => {});
            }

            if (typeof audioPath === "string") {
              getFirebaseAssetURL(audioPath)
                .then(url => {
                  if (url === undefined) return;
                  setAssignmentAudioFiles(files => {
                    const copy = [...files];
                    copy.push(url);
                    return copy;
                  });
                })
                .catch(() => {});
            }
          }
          const subjectNames = [
            ...new Set(
              data.data.sessionCreate.userAssignments
                .map(a => a.subject?.appName)
                .filter((name): name is string => typeof name === "string"),
            ),
          ];
          const subjects = sortSubjects(subjectNames);
          const envelopes = countEnvelopes(data.data);

          const easySubjects = new Set<string>();
          for (const easyAssignment of data.data.sessionCreate.userAssignments.slice(0, 2)) {
            const subjectName = easyAssignment.subject?.appName;
            if (typeof subjectName !== "string") continue;

            easySubjects.add(subjectName);
          }

          const mood = {
            mood: data.data.sessionCreate.lastSessionMood ?? null,
            moodText: data.data.sessionCreate.lastSessionMoodText ?? null,
            selfReflectionText: data.data.sessionCreate.lastSessionMoodSelfReflection ?? null,
          };

          setApiData({
            subjects,
            questionCount: data.data.sessionCreate.userAssignments.length,
            envelopes,
            easySubjectsCount: easySubjects.size,
            mood,
          });
        } else {
          setApiData({
            subjects: [],
            questionCount: 0,
            envelopes: null,
            easySubjectsCount: 0,
            mood: { mood: null, moodText: null, selfReflectionText: null },
          });
        }
      })
      .catch(console.error)
      .finally(() => {
        loadingData.current = false;
        setLoadingState({
          isUpdate: false,
          isInitialLoad: false,
        });
      });
  };

  const increaseTime = () => {
    selectTime(prev => {
      if (typeof prev !== "number") return prev;

      return prev + TIME_STEP;
    });
  };

  const decreaseTime = () => {
    selectTime(prev => {
      if (typeof prev === "number" && TIME_MIN + TIME_STEP <= prev) {
        return prev - TIME_STEP;
      }

      return prev;
    });
  };

  const handleStartButtonClick = (subject: string | null) => {
    if (assignmentAudioFiles.length > 0) {
      setSelectedSubjectToStart(subject);
      setIsSoundTestModalOpen(true);
      return;
    }

    startExercise(subject);
  };

  const handleSoundTestModalConfirm = () => {
    startExercise(selectedSubjectToStart);
  };

  const startExercise = (subject: string | null) => {
    if (loadingData.current) return;
    loadingData.current = true;

    TagManager.dataLayer({
      dataLayer: {
        event: "exercise-started",
        subject,
        duration: selectedTime,
        questionCount: apiData.questionCount,
      },
    });

    dispatch(resetAllTimers());
    dispatch(setSelectedTime(selectedTime));
    startSession()
      .then(response => {
        if ("data" in response && response.data) {
          const copy = [...response.data.sessionStart.userAssignments];

          if (copy.length === 0) {
            loadingData.current = false;
            setIsNoAssignmentsModalOpen(true);
            return;
          }

          if (subject !== null) {
            const startingAssignmentIndex = copy.findIndex(assignment => assignment.subject?.appName === subject);
            if (startingAssignmentIndex === 1) {
              copy.unshift(copy.splice(startingAssignmentIndex, 1)[0]);
            }
          }

          saveSessionToStorage(
            {
              sessionId: response.data.sessionStart.id,
              assignmentsData: copy,
              assignmentsResults: createEmptyResults(copy),
              spentTime: 0,
              newEnvelopes: {},
              selectedTime,
            },
            userEmail,
          );

          navigate(`/cviceni/${response.data.sessionStart.id}${location.search}`);
        }
      })
      .catch(console.error)
      .finally(() => {
        loadingData.current = false;
      });
  };

  const showNextTooltip = () => {
    if (
      processingTooltip.current ||
      highlightedElement.current !== null ||
      shownTooltips.current.length >= tooltipTexts.length
    )
      return;

    processingTooltip.current = true;

    for (let tooltipIndex = 0; tooltipIndex < tooltipTexts.length; tooltipIndex++) {
      if (shownTooltips.current.includes(tooltipIndex)) continue;

      const elementId = tooltipTexts[tooltipIndex]?.elementId;
      highlightedElement.current =
        elementId !== null && typeof elementId === "string" ? document.getElementById(elementId) : null;

      if (highlightedElement.current !== null && highlightedElement.current !== undefined) {
        shownTooltips.current.push(tooltipIndex);
        setHighlightClass(highlightedElement.current);
        setShownTooltipIndex(tooltipIndex);

        processingTooltip.current = false;

        return;
      }
    }

    processingTooltip.current = false;
  };

  const onTooltipClose = () => {
    if (highlightedElement.current !== null && highlightedElement.current !== undefined) {
      resetHighlightClass(highlightedElement.current);
    }

    highlightedElement.current = null;
    setShownTooltipIndex(null);
    showNextTooltip();
  };

  return (
    <div>
      <main className={"h-100 overflow-hidden d-flex flex-column justify-content-between justify-content-sm-start"}>
        <div className="page">
          {loadingState.isInitialLoad ? (
            <div className={"fullpage bg-primary-subtle"}>
              <Loading />
            </div>
          ) : (
            <>
              <div className="page__header">
                <LinkContainer to="/">
                  <Button variant={"close"} />
                </LinkContainer>
              </div>

              <div className="page__content" style={{ width: "100%" }}>
                <div id="timer">
                  <p className="text-center mb-12px">
                    <strong>{t("label.youGotMoreTime", { ns: "exercise" })}</strong>
                  </p>

                  <div className="d-flex flex-column align-items-center increment-group-wrapper mb-32px">
                    <div className="increment-group mb-32px">
                      <Button
                        variant={"primary"}
                        className={"btn-circular"}
                        onClick={decreaseTime}
                        disabled={
                          loadingState.isUpdate ||
                          typeof selectedTime !== "number" ||
                          selectedTime <= TIME_MIN ||
                          startingSession ||
                          apiData.subjects === null
                        }
                      >
                        <IconMinus />
                      </Button>
                      <p className={"h2 mb-0"}>
                        {selectedTime} {t("minuteShort", { ns: "common" })}
                      </p>
                      <Button
                        variant={"primary"}
                        className={"btn-circular"}
                        onClick={increaseTime}
                        disabled={
                          loadingState.isUpdate ||
                          typeof selectedTime !== "number" ||
                          startingSession ||
                          apiData.subjects === null ||
                          apiData.subjects.length === 0 ||
                          selectedTime >= TIME_MAX
                        }
                      >
                        <IconPlus />
                      </Button>
                    </div>

                    {apiData.envelopes === null ? null : (
                      <div className="mb-12px">
                        <span>Procvičovat budeš z těchto obálek:</span>
                        <EnvelopesOverview envelopesCount={apiData.envelopes} />{" "}
                      </div>
                    )}
                  </div>
                </div>

                <PrepareSupplies />

                {geometryImages.length > 0 ? <IsGeometry imagesUrl={geometryImages} /> : null}

                <div className="mt-32px">
                  <SelfReflection mood={apiData.mood} />
                </div>
              </div>

              <div className="page__footer">
                {apiData.subjects === null ? null : (
                  <>
                    <p className="text-center mb-12px">
                      {apiData.easySubjectsCount === 0 ? (
                        <>
                          <strong>{t("header.noAssignments")}</strong>
                        </>
                      ) : apiData.easySubjectsCount === 1 ? null : (
                        <strong>{t("header.startWith")}</strong>
                      )}
                    </p>

                    {apiData.easySubjectsCount === 0 ? null : apiData.easySubjectsCount === 1 ? (
                      <Button
                        type={"submit"}
                        className={"w-100 text-uppercase"}
                        onClick={() => {
                          handleStartButtonClick(null);
                        }}
                        disabled={loadingState.isInitialLoad || loadingState.isUpdate || startingSession}
                      >
                        {t("btn.start", { ns: "exercise" })}
                      </Button>
                    ) : (
                      apiData.subjects.map((subject, index) => (
                        <Button
                          key={subject}
                          type={"submit"}
                          className={"w-100 text-uppercase"}
                          onClick={() => {
                            handleStartButtonClick(subject);
                          }}
                          disabled={loadingState.isInitialLoad || loadingState.isUpdate || startingSession}
                          {...(index === 0 ? {} : { variant: "outline-primary" })}
                        >
                          {subject}
                        </Button>
                      ))
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <OnboardingTooltip
          key={shownTooltipIndex}
          data={shownTooltipIndex === null ? null : tooltipTexts[shownTooltipIndex]}
          onConfirm={onTooltipClose}
          highlightedElement={highlightedElement.current ?? null}
        />

        {isSoundTestModalOpen && (
          <SoundTestModal
            confirmText="Spustit trénink"
            onClose={() => {
              setIsSoundTestModalOpen(false);
            }}
            onConfirm={handleSoundTestModalConfirm}
          />
        )}

        {/* AIDEV-NOTE: `ariaLabel` — chybová hláška nemá v návrhu žádný nadpis, jen text v těle
            (typ 1, stejný případ jako Overtime.tsx). Jméno duplikuje viditelnou hlášku, jen se
            samo nevykresluje. [TD-344] */}
        <Lightbox
          show={isNoAssignmentsModalOpen}
          onHide={() => {
            setIsNoAssignmentsModalOpen(false);
          }}
          ariaLabel="Cvičení nelze spustit - server nevrátil žádné úlohy k procvičování."
        >
          <p>Cvičení nelze spustit - server nevrátil žádné úlohy k procvičování.</p>
        </Lightbox>
      </main>
    </div>
  );
};

export default Intro;
